import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Grid, Typography, CircularProgress, Autocomplete, TextField } from '@mui/material';
import ListTransfer from 'components/transfer/ListTransfer';
import { TransferItem } from '../types';
import { gettingPaycodeDistributionId } from 'utils/WorkRules';
import { useWorkRulesQuery } from 'hooks/workRules/useWorkRulesQuery';
import { useWorkRulesForm } from 'hooks/workRules/useWorkRulesForm';
import { useTranslation } from 'react-i18next';

interface Props {
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
    form: ReturnType<typeof useWorkRulesForm>;
}

const toTransferItems = (items: Array<any> | undefined): TransferItem[] => {
    if (!Array.isArray(items)) return [];

    return items
        .map((item) => {
            const id = item?.id ?? item?.schedule_desviation_rule_id ?? item?.overtime_rule_id ?? item?.zone_id;
            if (id === undefined || id === null) return null;
            return {
                id: Number(id),
                name: String(item?.name ?? ''),
            };
        })
        .filter((item): item is TransferItem => item !== null);
};

export const PaycodeDistributionTab = ({ mode, form }: Props) => {
    const { t } = useTranslation();
    const { dataList, loading: loadingCatalogs, loadData } = useWorkRulesQuery();
    const [loadingCombination, setLoadingCombination] = useState(false);
    const [schedulesDesviation, setSchedulesDesviation] = useState<TransferItem[]>([]);
    const [overtime, setOvertime] = useState<TransferItem[]>([]);
    const [zones, setZones] = useState<TransferItem[]>([]);
    const [scheduleDesviationSelected, setScheduleDesviationSelected] = useState<TransferItem[]>(toTransferItems(form.form.schedule_desviation_rules as Array<any>));
    const [overtimeSelected, setOvertimeSelected] = useState<TransferItem[]>(toTransferItems(form.form.overtime_rules as Array<any>));
    const [zoneSelected, setZoneSelected] = useState<TransferItem[]>(toTransferItems(form.form.zones as Array<any>));

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const request = async () => {
            setLoadingCombination(true);
            try {
                const response = await gettingPaycodeDistributionId(String(form.form.paycodes_distribution_id));
                const data = response.combination_rule;
                setSchedulesDesviation(data.schedule_desviation_rules || []);
                setZones(data.zones || []);
                setOvertime(data.overtime_rules || []);
            } catch (error) {
                console.error('Error fetching combination rule:', error);
            } finally {
                setLoadingCombination(false);
            }
        };

        if (form.form.paycodes_distribution_id) {
            request();
        }
    }, [form.form.paycodes_distribution_id]);

    const setSchedulesDesviationSelectedAndForm: Dispatch<SetStateAction<TransferItem[]>> = (value) => {
        setScheduleDesviationSelected((prev) => {
            const next = typeof value === 'function' ? value(prev) : value;
            form.update(
                'schedule_desviation_rules',
                next.map((item) => item.id),
            );
            return next;
        });
    };

    const setOvertimeSelectedAndForm: Dispatch<SetStateAction<TransferItem[]>> = (value) => {
        setOvertimeSelected((prev) => {
            const next = typeof value === 'function' ? value(prev) : value;
            form.update(
                'overtime_rules',
                next.map((item) => item.id),
            );
            return next;
        });
    };

    const setZoneSelectedAndForm: Dispatch<SetStateAction<TransferItem[]>> = (value) => {
        setZoneSelected((prev) => {
            const next = typeof value === 'function' ? value(prev) : value;
            form.update(
                'zones',
                next.map((item) => item.id),
            );
            return next;
        });
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Autocomplete
                        id="paycode_distribution"
                        options={dataList.paycode_distribution}
                        value={dataList.paycode_distribution.find((pay) => pay.id === form.form.paycodes_distribution_id) ?? null}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, val) => option.id === val.id}
                        onChange={(_, newValue) => {
                            form.update('paycodes_distribution_id', newValue?.id || 0);
                            if (mode === 'edit') {
                                setSchedulesDesviationSelectedAndForm([]);
                                setOvertimeSelectedAndForm([]);
                                setZoneSelectedAndForm([]);
                            }
                        }}
                        loading={loadingCatalogs}
                        disabled={loadingCatalogs}
                        fullWidth
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                required
                                label={t('settings.work_rules.fields.tabs.tab2.paycode_distribution')}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingCatalogs ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Typography variant="body2">
                        {t('settings.work_rules.fields.tabs.tab2.schedule_deviations')}
                        <br />
                        <br />
                    </Typography>

                    <ListTransfer
                        loading={loadingCombination}
                        allItems={schedulesDesviation}
                        right={scheduleDesviationSelected}
                        setRight={setSchedulesDesviationSelectedAndForm}
                        textLeft={t('settings.work_rules.fields.tabs.tab2.schedule_deviations_available')}
                        textRight={t('settings.work_rules.fields.tabs.tab2.selected_schedule_deviations')}
                        textSelected={t('settings.work_rules.fields.tabs.tab2.selected')}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Typography variant="body2">
                        {t('settings.work_rules.fields.tabs.tab2.extra_time')}
                        <br />
                        <br />
                    </Typography>

                    <ListTransfer
                        loading={loadingCombination}
                        allItems={overtime}
                        right={overtimeSelected}
                        setRight={setOvertimeSelectedAndForm}
                        textLeft={t('settings.work_rules.fields.tabs.tab2.extra_time_available')}
                        textRight={t('settings.work_rules.fields.tabs.tab2.selected_extra_time')}
                        textSelected={t('settings.work_rules.fields.tabs.tab2.selected')}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Typography variant="body2">
                        {t('settings.work_rules.fields.tabs.tab2.zones')}
                        <br />
                        <br />
                    </Typography>

                    <ListTransfer
                        loading={loadingCombination}
                        allItems={zones}
                        right={zoneSelected}
                        setRight={setZoneSelectedAndForm}
                        textLeft={t('settings.work_rules.fields.tabs.tab2.zones_available')}
                        textRight={t('settings.work_rules.fields.tabs.tab2.selected_zones')}
                        textSelected={t('settings.work_rules.fields.tabs.tab2.selected')}
                    />
                </Grid>
            </Grid>
        </>
    );
};
